import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    shipping_email: user?.email || "",
    shipping_address: "", shipping_city: "",
    shipping_country: "North Macedonia", shipping_postal: "", shipping_phone: "",
    payment_method: "cod", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Cart is empty"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({ product_id: it.id, quantity: it.quantity, variant_id: it.variant_id || null })),
      };
      const { data: order } = await api.post("/orders", payload);

      if (form.payment_method === "card") {
        const { data: session } = await api.post("/payments/checkout", {
          order_id: order.id,
          origin_url: window.location.origin,
        });
        window.location.href = session.checkout_url;
        return;
      }

      // Bank-transfer + COD both land on the dedicated confirmation page so
      // the customer sees the order number (and for bank_transfer, the IBAN
      // + reference + amount). The page clears the cart itself once loaded.
      toast.success(`${t.checkout.orderSuccess}${order.order_number}`);
      nav(`/order/confirmed/${order.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Order failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12" data-testid="checkout-page">
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-10">{t.checkout.title}</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-8">
          <section>
            <h2 className="font-display font-bold text-xl mb-4">{t.checkout.shipping}</h2>
            <div className="grid grid-cols-2 gap-4">
              <input required className="vt-input col-span-2" placeholder={t.checkout.name} value={form.shipping_name} onChange={upd("shipping_name")} data-testid="checkout-name" />
              <input required type="email" className="vt-input col-span-2" placeholder="Email address (order confirmation)" value={form.shipping_email} onChange={upd("shipping_email")} data-testid="checkout-email" />
              <input required className="vt-input col-span-2" placeholder={t.checkout.address} value={form.shipping_address} onChange={upd("shipping_address")} data-testid="checkout-address" />
              <input required className="vt-input" placeholder={t.checkout.city} value={form.shipping_city} onChange={upd("shipping_city")} data-testid="checkout-city" />
              <input className="vt-input" placeholder={t.checkout.postal} value={form.shipping_postal} onChange={upd("shipping_postal")} />
              <input required className="vt-input" placeholder={t.checkout.country} value={form.shipping_country} onChange={upd("shipping_country")} />
              <input required className="vt-input" placeholder={t.checkout.phone} value={form.shipping_phone} onChange={upd("shipping_phone")} data-testid="checkout-phone" />
              <textarea className="vt-input col-span-2" rows="3" placeholder={t.checkout.notes} value={form.notes} onChange={upd("notes")} />
            </div>
          </section>
          <section>
            <h2 className="font-display font-bold text-xl mb-4">{t.checkout.payment}</h2>
            <div className="space-y-2">
              <label className="flex items-center gap-3 border border-neutral-200 p-4 cursor-pointer has-[:checked]:border-black">
                <input type="radio" checked={form.payment_method === "card"} onChange={() => setForm({ ...form, payment_method: "card" })} data-testid="payment-card" />
                <div>
                  <div className="font-bold flex items-center gap-2">Card <span className="text-[10px] uppercase tracking-widest bg-[color:var(--vt-orange)] text-white px-1.5 py-0.5">Stripe</span></div>
                  <div className="text-xs text-neutral-500">Secure payment via Stripe. Visa, Mastercard, Amex.</div>
                </div>
              </label>
              <label className="flex items-center gap-3 border border-neutral-200 p-4 cursor-pointer has-[:checked]:border-black">
                <input type="radio" checked={form.payment_method === "cod"} onChange={() => setForm({ ...form, payment_method: "cod" })} data-testid="payment-cod" />
                <div><div className="font-bold">{t.checkout.cod}</div><div className="text-xs text-neutral-500">Pay in cash on delivery (North Macedonia only)</div></div>
              </label>
              <label className="flex items-center gap-3 border border-neutral-200 p-4 cursor-pointer has-[:checked]:border-black">
                <input type="radio" checked={form.payment_method === "bank_transfer"} onChange={() => setForm({ ...form, payment_method: "bank_transfer" })} data-testid="payment-bank" />
                <div><div className="font-bold">{t.checkout.bank}</div><div className="text-xs text-neutral-500">IBAN details will be sent by email</div></div>
              </label>
            </div>
          </section>
        </div>
        <aside className="border border-neutral-200 p-6 h-fit">
          <h3 className="font-display font-bold text-xl mb-4">Order Summary</h3>
          <div className="space-y-2 mb-4 text-sm">
            {items.map((it) => (
              <div key={it.id} className="flex justify-between">
                <span className="truncate">{it.name} × {it.quantity}</span>
                <span className="ml-2 font-semibold">€{(it.price * it.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-neutral-200 flex justify-between font-black text-2xl mb-4">
            <span>Total</span><span>€{total.toFixed(2)}</span>
          </div>
          <button type="submit" disabled={submitting} className="vt-btn-orange w-full" data-testid="checkout-submit">
            {submitting ? "..." : form.payment_method === "card" ? "Pay with Card →" : t.checkout.placeOrder}
          </button>
        </aside>
      </form>
    </div>
  );
}
